import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import publicApi from '../services/publicApi';

const API = publicApi;

const BLUE = '#4A7EB5';
const DARK = '#0F1923';

const JOB_LABELS: Record<string, string> = {
  roofing: '🏠 Roofing',
  concrete: '🪨 Concrete',
  asphalt: '🛣️ Asphalt',
};

const PAYMENT_OPTIONS = [
  { value: 'check', label: '🏦 Check' },
  { value: 'cash', label: '💵 Cash' },
  { value: 'card', label: '💳 Credit / Debit Card' },
  { value: 'bank_transfer', label: '🏛️ Bank Transfer (ACH)' },
];

type LineItem = {
  type: string;
  name: string;
  enabled: boolean;
  quantity: string | number;
  unit: string;
  unit_price: string | number;
};

type EstimateData = {
  id: string;
  homeowner_name: string;
  address: string;
  job_type: string;
  areas: { name: string; sqft: number; total: number }[];
  line_items?: LineItem[];
  total_price: number;
  price_per_sf: number;
  estimator_name: string;
  estimator_phone: string;
  status: string;
  proposed_scheduled_date?: string | null;
  proposed_scheduled_time?: string | null;
};

export default function SignPage() {
  const { token } = useParams<{ token: string }>();
  const sigRef = useRef<SignatureCanvas>(null);

  const [estimate, setEstimate] = useState<EstimateData | null>(null);
  const [loading, setLoading] = useState(true);
  const [alreadySigned, setAlreadySigned] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [hasSig, setHasSig] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return;
    API.get(`/public/sign/${token}`)
      .then(res => {
        if (res.data.already_signed) {
          setAlreadySigned(true);
        } else {
          setEstimate(res.data);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async () => {
    setError('');
    if (!agreed) { setError('Please check the acknowledgment box.'); return; }
    if (!hasSig || sigRef.current?.isEmpty()) { setError('Please sign before submitting.'); return; }
    if (!paymentMethod) { setError('Please select a payment method.'); return; }

    const signature_image = sigRef.current?.toDataURL('image/png') || '';
    const effectivePaymentMethod = paymentMethod;

    setSubmitting(true);
    try {
      await API.post(`/public/sign/${token}`, { signature_image, agreed, payment_method: effectivePaymentMethod });
      setDone(true);
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#8FA8C0' }}>Loading your estimate…</div>
      </div>
    </div>
  );

  if (notFound) return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
          <h2 style={{ color: '#fff', marginBottom: 8 }}>Estimate Not Found</h2>
          <p style={{ color: '#8FA8C0' }}>This link may have expired or been invalidated. Contact McMullen Browne for a new one.</p>
          <p style={{ color: '#8FA8C0', marginTop: 16 }}>📞 (302) 496-3967</p>
        </div>
      </div>
    </div>
  );

  if (alreadySigned) return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
          <h2 style={{ color: '#fff', marginBottom: 8 }}>Already Signed</h2>
          <p style={{ color: '#8FA8C0' }}>This estimate has already been signed. Check your email for a confirmation copy.</p>
          <p style={{ color: '#8FA8C0', marginTop: 16 }}>Questions? Call us at <strong style={{ color: BLUE }}>(302) 496-3967</strong></p>
        </div>
      </div>
    </div>
  );

  // ── Success screen ───────────────────────────────────────────────────────
  if (done) return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <div style={{ fontSize: 64, marginBottom: 20 }}>🎉</div>
          <h1 style={{ color: '#fff', fontSize: 26, fontWeight: 900, marginBottom: 8 }}>You're Locked In!</h1>
          <p style={{ color: '#8FA8C0', fontSize: 15, lineHeight: 1.6, maxWidth: 360, margin: '0 auto 24px' }}>
            Your estimate is signed. Check your email for a confirmation copy. Our team will contact you within 24 hours to schedule your project.
          </p>
          <div style={{ background: '#1A2B3C', borderRadius: 10, border: `1px solid #1E3A5F`, padding: '20px 24px', marginBottom: 24 }}>
            <p style={{ margin: 0, color: '#8FA8C0', fontSize: 13 }}>Questions or changes?</p>
            <p style={{ margin: '4px 0 0', color: '#fff', fontWeight: 700, fontSize: 15 }}>
              Call or text: <a href={`tel:${estimate?.estimator_phone}`} style={{ color: BLUE }}>{estimate?.estimator_phone}</a>
            </p>
            <p style={{ margin: '4px 0 0', color: '#8FA8C0', fontSize: 13 }}>{estimate?.estimator_name} — McMullen Browne Development</p>
          </div>
          <p style={{ color: '#4A6580', fontSize: 12 }}>Licensed &amp; Insured · 3-Year Workmanship Warranty</p>
        </div>
      </div>
    </div>
  );

  if (!estimate) return null;

  const depositAmt = (estimate.total_price * 0.20).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 });
  const totalFmt = estimate.total_price.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 });

  // ── Main signing page ────────────────────────────────────────────────────
  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img
            src="/logo-dark.png"
            alt="McMullen Browne Development"
            style={{ height: 36, width: 'auto', display: 'block' }}
          />
        </div>
      </div>

      <div style={styles.card}>
        {/* Title */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <span style={styles.badge}>{JOB_LABELS[estimate.job_type] || estimate.job_type}</span>
            <span style={{ ...styles.badge, background: '#1E3A5F', color: '#8FA8C0' }}>ESTIMATE</span>
          </div>
          <h1 style={{ color: '#fff', fontSize: 22, fontWeight: 900, margin: '0 0 4px' }}>Hi {estimate.homeowner_name},</h1>
          <p style={{ color: '#8FA8C0', fontSize: 14, margin: 0 }}>{estimate.address}</p>
        </div>

        {/* Total box */}
        <div style={styles.totalBox}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,.5)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Your Estimate Total</div>
          <div style={{ fontSize: 40, fontWeight: 900, color: BLUE, marginBottom: 4 }}>{totalFmt}</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,.5)' }}>
            {`20% deposit (${depositAmt}) required to schedule`}
          </div>
        </div>

        {/* Proposed scheduled date */}
        {estimate.proposed_scheduled_date && (
          <div style={{ background: '#1A3A2F', border: '1px solid #10B981', borderRadius: 12, padding: '16px 20px', marginBottom: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: '#10B981', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4, fontWeight: 600 }}>Start Date</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>
              {new Date(estimate.proposed_scheduled_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </div>
          </div>
        )}

        {/* Areas breakdown */}
        {(estimate.areas?.length > 0 || estimate.line_items?.some(li => li.enabled)) && (
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Scope of Work</div>
            {estimate.areas.map((area: any, i) => {
              const sqft = parseFloat(String(area.sqft)) || (parseFloat(String(area.length)) * parseFloat(String(area.width))) || 0;
              const total = parseFloat(String(area.total)) || (sqft * parseFloat(String(estimate.price_per_sf || 0))) || 0;
              const isLast = i === estimate.areas.length - 1 && !((estimate.line_items?.length ?? 0) > 0);
              return (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: isLast ? 'none' : '1px solid #1E3A5F' }}>
                  <div>
                    <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{area.name}</div>
                    <div style={{ color: '#8FA8C0', fontSize: 12 }}>{sqft > 0 ? sqft.toLocaleString() + ' sq ft' : ''}</div>
                  </div>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>{total > 0 ? '$' + total.toLocaleString() : '—'}</div>
                </div>
              );
            })}
            {/* Line items (add-ons) */}
            {estimate.line_items?.filter(li => parseFloat(String(li.quantity)) > 0).map((li, i, arr) => {
              const qty = parseFloat(String(li.quantity)) || 0;
              const price = parseFloat(String(li.unit_price)) || 0;
              const total = qty * price;
              return (
                <div key={`li-${i}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < arr.length - 1 ? '1px solid #1E3A5F' : 'none' }}>
                  <div>
                    <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{li.name}</div>
                    <div style={{ color: '#8FA8C0', fontSize: 12 }}>{qty.toLocaleString()} {li.unit}</div>
                  </div>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>${total.toLocaleString()}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Terms */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Terms &amp; Conditions</div>
          <div style={{ color: '#8FA8C0', fontSize: 13, lineHeight: 1.7 }}>
            {[
              'A 20% deposit is required to schedule. Work begins upon receipt of deposit.',
              'Final payment is due upon completion. Failure to pay within 7 days may result in a lien.',
              'Changes to scope require a written change order.',
              'Work may be rescheduled due to weather without penalty.',
              '3-year workmanship warranty from completion date.',
              'Cancellation within 48 hours of scheduled start forfeits the deposit.',
            ].map((t, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <span style={{ color: BLUE, flexShrink: 0 }}>•</span>
                <span>{t}</span>
              </div>
            ))}
          </div>
          {/* Agree checkbox */}
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 16, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={e => setAgreed(e.target.checked)}
              style={{ width: 18, height: 18, marginTop: 2, accentColor: BLUE, flexShrink: 0 }}
            />
            <span style={{ color: '#ccc', fontSize: 13, lineHeight: 1.5 }}>
              I have read and agree to the Terms &amp; Conditions above, and I authorize McMullen Browne Development LLC to proceed with the work at the quoted price.
            </span>
          </label>
        </div>

        {/* Payment method */}
        {(
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Deposit Payment Method</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {PAYMENT_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setPaymentMethod(opt.value)}
                  style={{
                    padding: '12px 10px',
                    borderRadius: 8,
                    border: `2px solid ${paymentMethod === opt.value ? BLUE : '#1E3A5F'}`,
                    background: paymentMethod === opt.value ? BLUE + '22' : '#1A2B3C',
                    color: paymentMethod === opt.value ? '#fff' : '#8FA8C0',
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {paymentMethod === 'card' || paymentMethod === 'bank_transfer' ? (
              <p style={{ color: '#8FA8C0', fontSize: 12, marginTop: 10 }}>
                💳 A secure payment link will be emailed to you after signing.
              </p>
            ) : null}
          </div>
        )}


        {/* Signature pad */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Your Signature</div>
          <p style={{ color: '#8FA8C0', fontSize: 13, marginBottom: 12 }}>Sign below using your mouse or finger.</p>
          <div style={{ border: `2px solid ${hasSig ? BLUE : '#1E3A5F'}`, borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
            <SignatureCanvas
              ref={sigRef}
              penColor="#0F1923"
              canvasProps={{ style: { width: '100%', height: 160, display: 'block' } }}
              onEnd={() => setHasSig(true)}
            />
          </div>
          {hasSig && (
            <button
              onClick={() => { sigRef.current?.clear(); setHasSig(false); }}
              style={{ background: 'none', border: 'none', color: '#EF4444', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 8, padding: 0 }}
            >
              ✕ Clear signature
            </button>
          )}
        </div>

        {/* Error */}
        {error && (
          <div style={{ background: '#EF444422', border: '1px solid #EF4444', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#EF4444', fontSize: 14 }}>
            {error}
          </div>
        )}

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            width: '100%',
            padding: '18px',
            background: submitting ? '#2A3A4C' : BLUE,
            color: '#fff',
            border: 'none',
            borderRadius: 12,
            fontSize: 17,
            fontWeight: 800,
            cursor: submitting ? 'default' : 'pointer',
            letterSpacing: '.3px',
          }}
        >
          {submitting ? 'Submitting…' : '✍️ Sign & Authorize Work'}
        </button>

        <p style={{ color: '#4A6580', fontSize: 11, textAlign: 'center', marginTop: 12 }}>
          By signing you are entering a legally binding agreement. McMullen Browne Development LLC · Licensed &amp; Insured
        </p>

        {/* Estimator contact */}
        <div style={{ background: '#1A2B3C', borderRadius: 10, border: '1px solid #1E3A5F', padding: '16px 20px', marginTop: 24, textAlign: 'center' }}>
          <p style={{ color: '#8FA8C0', fontSize: 12, margin: '0 0 4px' }}>Questions before signing?</p>
          <a href={`tel:${estimate.estimator_phone}`} style={{ color: BLUE, fontWeight: 700, fontSize: 15, textDecoration: 'none' }}>
            📞 {estimate.estimator_phone}
          </a>
          <p style={{ color: '#4A6580', fontSize: 12, margin: '4px 0 0' }}>{estimate.estimator_name} · McMullen Browne Development</p>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: DARK,
    padding: '0 0 60px',
  },
  header: {
    background: '#111D27',
    borderBottom: '1px solid #1E3A5F',
    padding: '16px 20px',
    position: 'sticky' as const,
    top: 0,
    zIndex: 10,
  },
  card: {
    maxWidth: 560,
    margin: '24px auto',
    padding: '0 16px',
  },
  totalBox: {
    background: '#111D27',
    border: `2px solid ${BLUE}`,
    borderRadius: 12,
    padding: '20px 24px',
    textAlign: 'center' as const,
    marginBottom: 24,
  },
  section: {
    background: '#111D27',
    border: '1px solid #1E3A5F',
    borderRadius: 12,
    padding: '18px 20px',
    marginBottom: 16,
  },
  sectionTitle: {
    color: '#fff',
    fontWeight: 800,
    fontSize: 14,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    marginBottom: 14,
  },
  badge: {
    background: BLUE + '33',
    color: BLUE,
    border: `1px solid ${BLUE}55`,
    borderRadius: 20,
    padding: '3px 12px',
    fontSize: 12,
    fontWeight: 700,
  },
};
